// frontend/src/components/admin/VersionManager.jsx
// ============================================================================
// GESTIONNAIRE DE VERSIONS HORAPILOT
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  Zap, Plus, Globe, Building2, Clock, Tag, ChevronDown,
  CheckCircle, AlertCircle, Edit2, Trash2, X, Save,
  Rocket, GitBranch, Wrench, Star, RefreshCw,
  Info,
} from 'lucide-react';
import superAdminApi from '../../services/superAdminApi';
import { Toast } from '../ui/Toast';
import { useTheme } from '../../contexts/ThemeContext';

// ============================================================================
// CONSTANTES
// ============================================================================

const TYPE_CONFIG = {
  major:   { label: 'Majeure',    color: '#7c3aed', icon: Star,       bg: '#7c3aed15' },
  feature: { label: 'Nouveauté', color: '#0ea5e9', icon: Rocket,     bg: '#0ea5e915' },
  fix:     { label: 'Correctif', color: '#10b981', icon: Wrench,     bg: '#10b98115' },
  hotfix:  { label: 'Hotfix',    color: '#ef4444', icon: AlertCircle, bg: '#ef444415' },
};

// ============================================================================
// SOUS-COMPOSANTS
// ============================================================================

const TypeBadge = ({ type }) => {
  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.fix;
  const Icon = cfg.icon;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700,
      background: cfg.bg, color: cfg.color }}>
      <Icon size={11} />
      {cfg.label}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, color, t }) => (
  <div style={{ background: t.bgCard, border: `1px solid ${t.border}`,
    borderRadius: 14, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
    <div style={{ width: 42, height: 42, borderRadius: 12, background: color + '15',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={20} style={{ color }} />
    </div>
    <div>
      <p style={{ fontSize: 11, fontWeight: 600, color: t.textMuted,
        textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>{label}</p>
      <p style={{ fontSize: 22, fontWeight: 800, color: t.text, margin: 0, lineHeight: 1.2 }}>{value}</p>
    </div>
  </div>
);

// ============================================================================
// MODAL CRÉATION / ÉDITION VERSION
// ============================================================================

const VersionFormModal = ({ version, onClose, onSave, t }) => {
  const isEdit = !!version;
  const [form, setForm] = useState({
    version:      version?.version      || '',
    type:         version?.type         || 'fix',
    titre:        version?.titre        || '',
    description:  version?.description  || '',
    deploy_global: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const handleSubmit = async () => {
    if (!form.version.trim()) { setError('Le numéro de version est requis'); return; }
    if (!form.titre.trim())   { setError('Le titre est requis'); return; }
    setSaving(true);
    try {
      await onSave(form);
    } catch (e) {
      setError(e.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const inp = {
    width: '100%', padding: '10px 14px', borderRadius: 10,
    border: `1.5px solid ${t.border}`, background: t.bgInput,
    color: t.text, fontSize: 13, fontFamily: 'DM Sans, sans-serif',
    outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200,
      background: 'rgba(10,15,30,0.72)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 520, borderRadius: 20,
        background: t.bgCard, border: `1px solid ${t.border}`,
        boxShadow: '0 40px 80px rgba(0,0,0,0.25)', overflow: 'hidden',
        fontFamily: 'DM Sans, sans-serif' }}>

        {/* Barre top */}
        <div style={{ height: 4, background: 'linear-gradient(90deg, #448696, #fed774)' }} />

        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${t.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: '#44869615',
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Zap size={18} color="#448696" />
            </div>
            <div>
              <p style={{ fontSize: 15, fontWeight: 800, color: t.text, margin: 0 }}>
                {isEdit ? 'Modifier la version' : 'Nouvelle version'}
              </p>
              <p style={{ fontSize: 11, color: t.textMuted, margin: 0 }}>
                {isEdit ? `v${version.version}` : 'Créer et optionnellement déployer'}
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent',
            cursor: 'pointer', color: t.textMuted, padding: 6, borderRadius: 8 }}>
            <X size={18} />
          </button>
        </div>

        {/* Corps */}
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {error && (
            <div style={{ padding: '10px 14px', borderRadius: 10, background: '#fef2f2',
              border: '1px solid #fecaca', display: 'flex', gap: 8, alignItems: 'center' }}>
              <AlertCircle size={14} color="#dc2626" />
              <p style={{ fontSize: 12, color: '#dc2626', margin: 0, fontWeight: 600 }}>{error}</p>
            </div>
          )}

          {/* Version + Type */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: t.textMuted,
                textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: 6 }}>
                Numéro de version *
              </label>
              <input value={form.version} onChange={e => setForm(p => ({ ...p, version: e.target.value }))}
                disabled={isEdit} placeholder="ex: 1.0.2"
                style={{ ...inp, fontFamily: 'monospace', fontWeight: 800,
                  opacity: isEdit ? 0.6 : 1, cursor: isEdit ? 'not-allowed' : 'text' }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: t.textMuted,
                textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: 6 }}>
                Type *
              </label>
              <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}
                style={{ ...inp, cursor: 'pointer', appearance: 'none' }}>
                {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
                  <option key={key} value={key}>{cfg.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Titre */}
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: t.textMuted,
              textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: 6 }}>
              Titre *
            </label>
            <input value={form.titre} onChange={e => setForm(p => ({ ...p, titre: e.target.value }))}
              placeholder="ex: ProspectionHub & Sécurité" style={inp} />
          </div>

          {/* Description / Changelog */}
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: t.textMuted,
              textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: 6 }}>
              Changelog / Notes
            </label>
            <textarea value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              rows={5} placeholder="Décrivez les changements, nouvelles fonctionnalités, corrections..."
              style={{ ...inp, resize: 'vertical', lineHeight: 1.6 }} />
          </div>

          {/* Deploy global — seulement à la création */}
          {!isEdit && (
            <div style={{ padding: '12px 16px', borderRadius: 12,
              background: '#44869608', border: `1px solid ${t.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: 13, fontWeight: 700, color: t.text, margin: 0 }}>
                  Déployer sur toutes les entreprises
                </p>
                <p style={{ fontSize: 11, color: t.textMuted, margin: '2px 0 0' }}>
                  Met à jour `version_app` dans toutes les licences
                </p>
              </div>
              <button type="button"
                onClick={() => setForm(p => ({ ...p, deploy_global: !p.deploy_global }))}
                style={{ width: 44, height: 24, borderRadius: 99, border: 'none',
                  cursor: 'pointer', position: 'relative', transition: 'background 0.2s',
                  background: form.deploy_global ? '#448696' : '#d1d5db', flexShrink: 0 }}>
                <span style={{ position: 'absolute', top: 2, transition: 'left 0.2s',
                  left: form.deploy_global ? 22 : 2, width: 20, height: 20,
                  borderRadius: '50%', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} />
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px 20px', borderTop: `1px solid ${t.border}`,
          display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose}
            style={{ padding: '9px 20px', borderRadius: 10, border: `1px solid ${t.border}`,
              background: 'transparent', color: t.textSecondary, fontSize: 13,
              fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
            Annuler
          </button>
          <button onClick={handleSubmit} disabled={saving}
            style={{ padding: '9px 22px', borderRadius: 10, border: 'none',
              background: 'linear-gradient(135deg, #448696, #367080)',
              color: 'white', fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 8,
              fontFamily: 'DM Sans, sans-serif', boxShadow: '0 4px 12px rgba(68,134,150,0.35)' }}>
            {saving ? (
              <><RefreshCw size={14} style={{ animation: 'spin 0.8s linear infinite' }} />
                Enregistrement...</>
            ) : (
              <><Save size={14} />{isEdit ? 'Modifier' : form.deploy_global ? 'Créer & Déployer' : 'Créer'}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

const VersionManager = () => {
  const t = useTheme();

  const [versions,     setVersions]     = useState([]);
  const [entreprises,  setEntreprises]  = useState([]);
  const [current,      setCurrent]      = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [showModal,    setShowModal]    = useState(false);
  const [editingV,     setEditingV]     = useState(null);
  const [deploying,    setDeploying]    = useState(false);
  const [activeTab,    setActiveTab]    = useState('historique');
  const [toast,        setToast]        = useState({ show: false, message: '', type: '' });

  const showToast = (message, type = 'success') => setToast({ show: true, message, type });
  useEffect(() => {
    if (toast.show) {
      const t = setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
      return () => clearTimeout(t);
    }
  }, [toast.show]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [vRes, eRes, cRes] = await Promise.all([
        superAdminApi.versions.getAll(),
        superAdminApi.versions.getEntreprises(),
        superAdminApi.versions.getCurrent(),
      ]);
      if (vRes.success) setVersions(vRes.data || []);
      if (eRes.success) setEntreprises(eRes.data || []);
      if (cRes.success) setCurrent(cRes.data);
    } catch (e) { showToast('Erreur de chargement', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const handleCreate = async (form) => {
    const res = await superAdminApi.versions.create(form);
    if (!res.success) throw new Error(res.message);
    showToast(res.message);
    setShowModal(false);
    await loadAll();
  };

  const handleEdit = async (form) => {
    const res = await superAdminApi.versions.update(editingV.id, form);
    if (!res.success) throw new Error(res.message);
    showToast('Version modifiée');
    setEditingV(null);
    await loadAll();
  };

  const handleDelete = async (v) => {
    if (!window.confirm(`Supprimer la version ${v.version} ?`)) return;
    const res = await superAdminApi.versions.delete(v.id);
    if (res.success) { showToast('Version supprimée'); await loadAll(); }
    else showToast(res.message, 'error');
  };

  const handleDeployGlobal = async (v) => {
    if (!window.confirm(`Déployer v${v.version} sur TOUTES les entreprises ?`)) return;
    setDeploying(v.id);
    try {
      const res = await superAdminApi.versions.deployGlobal(v.version, v.description);
      if (res.success) { showToast(res.message); await loadAll(); }
      else showToast(res.message, 'error');
    } finally { setDeploying(null); }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const upToDateCount = entreprises.filter(e => e.version_app === current?.version).length;
  const outdatedCount = entreprises.filter(e => e.version_app !== current?.version).length;

  const TABS = [
    { key: 'historique', label: 'Historique des versions', icon: GitBranch },
    { key: 'entreprises', label: 'État par entreprise', icon: Building2 },
  ];

  // ── Styles communs ──
  const cardStyle = {
    background: t.bgCard, border: `1px solid ${t.border}`,
    borderRadius: 14, fontFamily: 'DM Sans, sans-serif',
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 80 }}>
      <RefreshCw size={24} color="#448696" style={{ animation: 'spin 1s linear infinite' }} />
    </div>
  );

  return (
    <div style={{ fontFamily: 'DM Sans, sans-serif' }}>

      {/* ── Header ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12,
              background: 'linear-gradient(135deg, #448696, #367080)',
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Info size={22} color="white" />
            </div>
            <div>
              <h1 style={{ fontSize: 20, fontWeight: 800, color: t.text, margin: 0 }}>
                Versions HoraPilot
              </h1>
              <p style={{ fontSize: 12, color: t.textMuted, margin: 0 }}>
                Gestion et déploiement des versions
              </p>
            </div>
          </div>
          <button onClick={() => setShowModal(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 18px', borderRadius: 12, border: 'none',
              background: 'linear-gradient(135deg, #448696, #367080)',
              color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(68,134,150,0.35)', fontFamily: 'DM Sans, sans-serif' }}>
            <Plus size={16} />
            Nouvelle version
          </button>
        </div>
      </div>

      {/* ── Stats ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
        <StatCard icon={Zap}          label="Version actuelle" value={`v${current?.version || '—'}`} color="#448696" t={t} />
        <StatCard icon={GitBranch}    label="Versions totales" value={versions.length}               color="#7c3aed" t={t} />
        <StatCard icon={CheckCircle}  label="À jour"           value={upToDateCount}                  color="#10b981" t={t} />
        <StatCard icon={AlertCircle}  label="En retard"        value={outdatedCount}                  color="#f59e0b" t={t} />
      </div>

      {/* ── Version courante banner ── */}
      {current && (
        <div style={{ ...cardStyle, padding: '18px 22px', marginBottom: 24,
          borderLeft: '4px solid #448696', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: '#44869615',
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Rocket size={22} color="#448696" />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                <span style={{ fontSize: 18, fontWeight: 900, color: t.text, fontFamily: 'monospace' }}>
                  v{current.version}
                </span>
                <TypeBadge type={current.type} />
                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99,
                  background: '#44869615', color: '#448696', fontWeight: 700 }}>
                  ACTUELLE
                </span>
              </div>
              <p style={{ fontSize: 13, fontWeight: 600, color: t.text, margin: '0 0 2px' }}>
                {current.titre}
              </p>
              <p style={{ fontSize: 11, color: t.textMuted, margin: 0 }}>
                Déployée le {formatDate(current.deployed_at)} par {current.deployed_by}
              </p>
            </div>
          </div>
          <button onClick={() => handleDeployGlobal(current)}
            disabled={deploying === current.id}
            style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px',
              borderRadius: 10, border: `1px solid ${'#44869630'}`, background: '#44869610',
              color: '#448696', fontSize: 12, fontWeight: 700, cursor: 'pointer',
              fontFamily: 'DM Sans, sans-serif', opacity: deploying === current.id ? 0.6 : 1 }}>
            {deploying === current.id
              ? <RefreshCw size={14} style={{ animation: 'spin 0.8s linear infinite' }} />
              : <Globe size={14} />}
            Déployer sur tous
          </button>
        </div>
      )}

      {/* ── Onglets ── */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${t.border}`, marginBottom: 20 }}>
        {TABS.map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.key;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{ display: 'flex', alignItems: 'center', gap: 6,
                padding: '10px 16px', border: 'none', background: 'transparent',
                borderBottom: `2px solid ${active ? '#448696' : 'transparent'}`,
                color: active ? '#448696' : t.textMuted, fontSize: 13,
                fontWeight: active ? 700 : 500, cursor: 'pointer', marginBottom: -1,
                fontFamily: 'DM Sans, sans-serif' }}>
              <Icon size={15} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ── ONGLET HISTORIQUE ── */}
      {activeTab === 'historique' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {versions.length === 0 ? (
            <div style={{ ...cardStyle, padding: 48, textAlign: 'center' }}>
              <GitBranch size={40} color={t.textMuted} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
              <p style={{ color: t.textMuted, fontSize: 14 }}>Aucune version enregistrée</p>
            </div>
          ) : versions.map((v, i) => (
            <div key={v.id} style={{ ...cardStyle, padding: '16px 20px',
              opacity: v.is_current ? 1 : 0.75,
              animation: `hpFadeInUp 0.2s ease-out ${i * 30}ms both` }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 16, fontWeight: 900, color: t.text, fontFamily: 'monospace' }}>
                      v{v.version}
                    </span>
                    <TypeBadge type={v.type} />
                    {v.is_current && (
                      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 99,
                        background: '#44869615', color: '#448696', fontWeight: 800,
                        textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Actuelle
                      </span>
                    )}
                  </div>
                  <p style={{ fontSize: 13, fontWeight: 700, color: t.text, margin: '0 0 4px' }}>
                    {v.titre}
                  </p>
                  {v.description && (
                    <p style={{ fontSize: 12, color: t.textSecondary, margin: '0 0 6px',
                      lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                      {v.description}
                    </p>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <span style={{ fontSize: 11, color: t.textMuted, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Clock size={11} />
                      {formatDate(v.deployed_at)}
                    </span>
                    {v.deployed_by && (
                      <span style={{ fontSize: 11, color: t.textMuted }}>
                        par {v.deployed_by}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => handleDeployGlobal(v)}
                    disabled={!!deploying}
                    title="Déployer sur toutes les entreprises"
                    style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${t.border}`,
                      background: t.bgInput, color: '#448696', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700,
                      fontFamily: 'DM Sans, sans-serif', opacity: deploying === v.id ? 0.5 : 1 }}>
                    {deploying === v.id
                      ? <RefreshCw size={12} style={{ animation: 'spin 0.8s linear infinite' }} />
                      : <Globe size={12} />}
                    Déployer
                  </button>
                  <button onClick={() => setEditingV(v)}
                    style={{ padding: 7, borderRadius: 8, border: `1px solid ${t.border}`,
                      background: t.bgInput, color: t.textMuted, cursor: 'pointer' }}>
                    <Edit2 size={14} />
                  </button>
                  {!v.is_current && (
                    <button onClick={() => handleDelete(v)}
                      style={{ padding: 7, borderRadius: 8, border: '1px solid #fecaca',
                        background: '#fef2f2', color: '#dc2626', cursor: 'pointer' }}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── ONGLET ENTREPRISES ── */}
      {activeTab === 'entreprises' && (
        <div style={{ ...cardStyle, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${t.border}` }}>
                {['Entreprise', 'Version installée', 'Licence', 'Statut', 'Dernière MAJ'].map((h, i) => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left',
                    fontSize: 10, fontWeight: 700, color: t.textMuted,
                    textTransform: 'uppercase', letterSpacing: '0.5px',
                    background: t.bgTableHeader }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entreprises.map((e, i) => {
                const isUpToDate = e.version_app === current?.version;
                return (
                  <tr key={e.id} style={{ borderBottom: `1px solid ${t.borderLight}`,
                    animation: `hpFadeInUp 0.2s ease-out ${i * 20}ms both` }}
                    onMouseEnter={el => el.currentTarget.style.backgroundColor = t.bgTableRowHover}
                    onMouseLeave={el => el.currentTarget.style.backgroundColor = 'transparent'}>

                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8,
                          background: 'linear-gradient(135deg, #448696, #367080)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Building2 size={15} color="white" />
                        </div>
                        <div>
                          <p style={{ fontSize: 13, fontWeight: 700, color: t.text, margin: 0 }}>
                            {e.company_name}
                          </p>
                          <p style={{ fontSize: 11, color: t.textMuted, margin: 0 }}>
                            {e.company_slug}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: t.text,
                          fontFamily: 'monospace' }}>
                          v{e.version_app || '—'}
                        </span>
                        {!isUpToDate && e.version_app && (
                          <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 99,
                            background: '#fef3c7', color: '#d97706', fontWeight: 700 }}>
                            Retard
                          </span>
                        )}
                        {isUpToDate && (
                          <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 99,
                            background: '#f0fdf4', color: '#059669', fontWeight: 700 }}>
                            À jour
                          </span>
                        )}
                      </div>
                    </td>

                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: t.textSecondary,
                        textTransform: 'capitalize' }}>
                        {e.type_licence || '—'}
                      </span>
                    </td>

                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px',
                        borderRadius: 99,
                        background: e.statut === 'active' ? '#f0fdf4' : '#fef2f2',
                        color: e.statut === 'active' ? '#059669' : '#dc2626' }}>
                        {e.statut === 'active' ? 'Active' : e.statut || '—'}
                      </span>
                    </td>

                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontSize: 12, color: t.textMuted }}>
                        {formatDate(e.version_updated_at)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {entreprises.length === 0 && (
            <div style={{ padding: 48, textAlign: 'center' }}>
              <p style={{ color: t.textMuted, fontSize: 14 }}>Aucune entreprise trouvée</p>
            </div>
          )}
        </div>
      )}

      {/* ── Modals ── */}
      {showModal && (
        <VersionFormModal t={t} onClose={() => setShowModal(false)} onSave={handleCreate} />
      )}
      {editingV && (
        <VersionFormModal t={t} version={editingV} onClose={() => setEditingV(null)} onSave={handleEdit} />
      )}

      {/* Toast */}
      {toast.show && (
        <Toast show={toast.show} message={toast.message} type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: '' })} />
      )}

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
};

export default VersionManager;